// Object type identifiers - extensible enum pattern
export const ObjectType = {
    NONE: 0,
    // Natural objects (1-99)
    TREE_OAK: 1,
    TREE_PINE: 2,
    TREE_BIRCH: 3,
    BUSH: 10,
    ROCK_SMALL: 20,
    ROCK_MEDIUM: 21,
    ROCK_LARGE: 22,
    BOULDER: 23,
    STICKS: 30,
    BRANCH: 31,
    MUSHROOM_CLUSTER: 32,
    FALLEN_LOG: 33,
    WILD_FLOWERS: 34,
    TREE_STUMP: 35,
    FERN: 36,
    // Ruins/Graveyard (50-59)
    GRAVESTONE: 50,
    RUINED_CRYPT: 51,
    GRAVE_MOUND: 52,
    ANCIENT_RUINS: 53, // Large ruin structure spawning many skeletons
    // Structures (100-199)
    WALL_WOOD: 100,
    WALL_STONE: 101,
    WALL_METAL: 102,
    DOOR_WOOD: 110,
    DOOR_METAL: 111,
    FLOOR_WOOD: 120,
    FLOOR_STONE: 121,
    // Furniture (200-299)
    BED: 200,
    TABLE: 201,
    CHAIR: 202,
    SLEEPING_PAD: 203, // Primitive sleeping pad for emergency rescue
    STORAGE_CRATE: 210,
    STORAGE_SHELF: 211,
    // Workstations (300-399)
    CRAFTING_TABLE: 300,
    ANVIL: 301,
    CAMPFIRE: 302,
    COOKING_STOVE: 303,
    FURNACE: 304,
    // Resources (400-499) - Small ore deposits (finite)
    ORE_IRON: 400,
    ORE_GOLD: 401,
    ORE_COPPER: 402,
    ORE_TIN: 403,
    ORE_COAL: 404,
    ORE_SILVER: 405,
    ORE_MITHRIL: 406,
    ORE_STARMETAL: 407,
    // Fishing Spots (420-423)
    FISHING_SPOT_SHORE: 420,
    FISHING_SPOT_RIVER: 421,
    FISHING_SPOT_OCEAN: 422,
    FISHING_SPOT_SWAMP: 423,
    // Large ore deposits (respawn) - 410-419
    ORE_TIN_LARGE: 410,
    ORE_COPPER_LARGE: 411,
    ORE_IRON_LARGE: 412,
    ORE_COAL_LARGE: 413,
    ORE_SILVER_LARGE: 414,
    ORE_GOLD_LARGE: 415,
    ORE_MITHRIL_LARGE: 416,
    ORE_STARMETAL_LARGE: 417,
    // Territory Flags (500-509)
    CLAIM_FLAG_HOME: 500,
    CLAIM_FLAG_OUTPOST: 501,
};
// Object categories for grouping behavior
export const ObjectCategory = {
    NONE: 0,
    NATURAL: 1,
    STRUCTURE: 2,
    FURNITURE: 3,
    WORKSTATION: 4,
    RESOURCE: 5,
    TERRITORY: 6,
    RUINS: 7, // Graveyards, crypts, etc. that spawn undead
};
// Object layer for rendering order and collision
export const ObjectLayer = {
    FLOOR: 0, // Overlays tile (floors, rugs)
    GROUND: 1, // On the ground (items, low plants)
    OBJECT: 2, // Main objects (furniture, rocks)
    TALL: 3, // Tall objects (trees, walls)
};
// Default properties for all object types
export const OBJECT_PROPERTIES = {
    [ObjectType.NONE]: {
        name: 'none',
        category: ObjectCategory.NONE,
        layer: ObjectLayer.GROUND,
        blockMovement: false,
        blockSight: false,
        destructible: false,
        maxHealth: 0,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: false,
    },
    // Trees
    [ObjectType.TREE_OAK]: {
        name: 'Oak Tree',
        category: ObjectCategory.NATURAL,
        layer: ObjectLayer.TALL,
        blockMovement: true,
        blockSight: true,
        destructible: true,
        maxHealth: 200,
        flammable: true,
        providesShade: true,
        width: 1,
        height: 2,
        harvestable: true,
        harvestYield: { resourceType: 'wood', minAmount: 20, maxAmount: 40 },
        buildable: false,
    },
    [ObjectType.TREE_PINE]: {
        name: 'Pine Tree',
        category: ObjectCategory.NATURAL,
        layer: ObjectLayer.TALL,
        blockMovement: true,
        blockSight: true,
        destructible: true,
        maxHealth: 180,
        flammable: true,
        providesShade: true,
        width: 1,
        height: 2,
        harvestable: true,
        harvestYield: { resourceType: 'wood', minAmount: 15, maxAmount: 35 },
        buildable: false,
    },
    [ObjectType.TREE_BIRCH]: {
        name: 'Birch Tree',
        category: ObjectCategory.NATURAL,
        layer: ObjectLayer.TALL,
        blockMovement: true,
        blockSight: true,
        destructible: true,
        maxHealth: 150,
        flammable: true,
        providesShade: true,
        width: 1,
        height: 2,
        harvestable: true,
        harvestYield: { resourceType: 'wood', minAmount: 12, maxAmount: 28 },
        buildable: false,
    },
    [ObjectType.BUSH]: {
        name: 'Bush',
        category: ObjectCategory.NATURAL,
        layer: ObjectLayer.OBJECT,
        blockMovement: false,
        blockSight: false,
        destructible: true,
        maxHealth: 300,
        flammable: true,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'berries', minAmount: 10, maxAmount: 15 },
        buildable: false,
    },
    // Rocks
    [ObjectType.ROCK_SMALL]: {
        name: 'Small Rock',
        category: ObjectCategory.NATURAL,
        layer: ObjectLayer.GROUND,
        blockMovement: false,
        blockSight: false,
        destructible: true,
        maxHealth: 100,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'stone', minAmount: 5, maxAmount: 10 },
        buildable: false,
    },
    [ObjectType.ROCK_MEDIUM]: {
        name: 'Medium Rock',
        category: ObjectCategory.NATURAL,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 200,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'stone', minAmount: 15, maxAmount: 25 },
        buildable: false,
    },
    [ObjectType.ROCK_LARGE]: {
        name: 'Large Rock',
        category: ObjectCategory.NATURAL,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: true,
        destructible: true,
        maxHealth: 400,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'stone', minAmount: 30, maxAmount: 50 },
        buildable: false,
    },
    [ObjectType.BOULDER]: {
        name: 'Boulder',
        category: ObjectCategory.NATURAL,
        layer: ObjectLayer.TALL,
        blockMovement: true,
        blockSight: true,
        destructible: true,
        maxHealth: 800,
        flammable: false,
        providesShade: true,
        width: 2,
        height: 2,
        harvestable: true,
        harvestYield: { resourceType: 'stone', minAmount: 80, maxAmount: 120 },
        buildable: false,
    },
    // Sticks & Branches (ground debris)
    [ObjectType.STICKS]: {
        name: 'Sticks',
        category: ObjectCategory.NATURAL,
        layer: ObjectLayer.GROUND,
        blockMovement: false,
        blockSight: false,
        destructible: true,
        maxHealth: 30,
        flammable: true,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'wood', minAmount: 12, maxAmount: 30 },
        buildable: false,
    },
    [ObjectType.BRANCH]: {
        name: 'Branch',
        category: ObjectCategory.NATURAL,
        layer: ObjectLayer.GROUND,
        blockMovement: false,
        blockSight: false,
        destructible: true,
        maxHealth: 50,
        flammable: true,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'wood', minAmount: 18, maxAmount: 36 },
        buildable: false,
    },
    // Forest undergrowth
    [ObjectType.MUSHROOM_CLUSTER]: {
        name: 'Mushroom Cluster',
        category: ObjectCategory.NATURAL,
        layer: ObjectLayer.GROUND,
        blockMovement: false,
        blockSight: false,
        destructible: true,
        maxHealth: 20,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'berries', minAmount: 3, maxAmount: 8 },
        buildable: false,
    },
    [ObjectType.FALLEN_LOG]: {
        name: 'Fallen Log',
        category: ObjectCategory.NATURAL,
        layer: ObjectLayer.GROUND,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 120,
        flammable: true,
        providesShade: false,
        width: 2,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'wood', minAmount: 15, maxAmount: 30 },
        buildable: false,
    },
    [ObjectType.WILD_FLOWERS]: {
        name: 'Wild Flowers',
        category: ObjectCategory.NATURAL,
        layer: ObjectLayer.GROUND,
        blockMovement: false,
        blockSight: false,
        destructible: true,
        maxHealth: 10,
        flammable: true,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: false,
    },
    [ObjectType.TREE_STUMP]: {
        name: 'Tree Stump',
        category: ObjectCategory.NATURAL,
        layer: ObjectLayer.GROUND,
        blockMovement: false,
        blockSight: false,
        destructible: true,
        maxHealth: 80,
        flammable: true,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'wood', minAmount: 5, maxAmount: 12 },
        buildable: false,
    },
    [ObjectType.FERN]: {
        name: 'Fern',
        category: ObjectCategory.NATURAL,
        layer: ObjectLayer.GROUND,
        blockMovement: false,
        blockSight: false,
        destructible: true,
        maxHealth: 15,
        flammable: true,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: false,
    },
    // Ruins/Graveyard
    [ObjectType.GRAVESTONE]: {
        name: 'Gravestone',
        category: ObjectCategory.RUINS,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 150,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: false,
        // Spawns skeletons (NpcType.SKELETON = 110)
        spawnsNpc: 110,
        maxSpawned: 2,
        spawnRadius: 8,
        respawnDelayTicks: 6000, // ~5 minutes at 20 tps
    },
    [ObjectType.RUINED_CRYPT]: {
        name: 'Ruined Crypt',
        category: ObjectCategory.RUINS,
        layer: ObjectLayer.TALL,
        blockMovement: true,
        blockSight: true,
        destructible: true,
        maxHealth: 500,
        flammable: false,
        providesShade: true,
        width: 2,
        height: 2,
        harvestable: false,
        buildable: false,
        // Spawns more skeletons
        spawnsNpc: 110,
        maxSpawned: 4,
        spawnRadius: 12,
        respawnDelayTicks: 4800, // ~4 minutes at 20 tps
    },
    [ObjectType.GRAVE_MOUND]: {
        name: 'Grave Mound',
        category: ObjectCategory.RUINS,
        layer: ObjectLayer.GROUND,
        blockMovement: false,
        blockSight: false,
        destructible: true,
        maxHealth: 50,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: false,
        // Decorative, no spawning
    },
    [ObjectType.ANCIENT_RUINS]: {
        name: 'Ancient Ruins',
        category: ObjectCategory.RUINS,
        layer: ObjectLayer.TALL,
        blockMovement: true,
        blockSight: true,
        destructible: true,
        maxHealth: 1000,
        flammable: false,
        providesShade: true,
        width: 3,
        height: 3,
        harvestable: false,
        buildable: false,
        // Spawns many skeletons - dangerous location
        spawnsNpc: 110, // SKELETON
        maxSpawned: 8,
        spawnRadius: 16,
        respawnDelayTicks: 3600, // ~3 minutes at 20 tps
    },
    // Walls
    [ObjectType.WALL_WOOD]: {
        name: 'Wooden Wall',
        category: ObjectCategory.STRUCTURE,
        layer: ObjectLayer.TALL,
        blockMovement: true,
        blockSight: true,
        destructible: true,
        maxHealth: 150,
        flammable: true,
        providesShade: true,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: true,
        buildCost: [{ resourceType: 'wood', amount: 10 }],
        buildTime: 100,
    },
    [ObjectType.WALL_STONE]: {
        name: 'Stone Wall',
        category: ObjectCategory.STRUCTURE,
        layer: ObjectLayer.TALL,
        blockMovement: true,
        blockSight: true,
        destructible: true,
        maxHealth: 400,
        flammable: false,
        providesShade: true,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: true,
        buildCost: [{ resourceType: 'stone', amount: 15 }],
        buildTime: 150,
    },
    [ObjectType.WALL_METAL]: {
        name: 'Metal Wall',
        category: ObjectCategory.STRUCTURE,
        layer: ObjectLayer.TALL,
        blockMovement: true,
        blockSight: true,
        destructible: true,
        maxHealth: 600,
        flammable: false,
        providesShade: true,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: true,
        buildCost: [{ resourceType: 'metal', amount: 10 }],
        buildTime: 200,
    },
    // Doors
    [ObjectType.DOOR_WOOD]: {
        name: 'Wooden Door',
        category: ObjectCategory.STRUCTURE,
        layer: ObjectLayer.TALL,
        blockMovement: true, // Closed by default; owner pawns auto-open while passing
        blockSight: true,
        destructible: true,
        maxHealth: 100,
        flammable: true,
        providesShade: true,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: true,
        buildCost: [{ resourceType: 'wood', amount: 15 }],
        buildTime: 80,
    },
    [ObjectType.DOOR_METAL]: {
        name: 'Metal Door',
        category: ObjectCategory.STRUCTURE,
        layer: ObjectLayer.TALL,
        blockMovement: true, // Closed by default; owner pawns auto-open while passing
        blockSight: true,
        destructible: true,
        maxHealth: 300,
        flammable: false,
        providesShade: true,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: false,
        buildCost: [{ resourceType: 'metal', amount: 15 }],
        buildTime: 150,
    },
    // Floors
    [ObjectType.FLOOR_WOOD]: {
        name: 'Wooden Floor',
        category: ObjectCategory.STRUCTURE,
        layer: ObjectLayer.FLOOR,
        blockMovement: false,
        blockSight: false,
        destructible: true,
        maxHealth: 50,
        flammable: true,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: false,
        buildCost: [{ resourceType: 'wood', amount: 3 }],
        buildTime: 20,
    },
    [ObjectType.FLOOR_STONE]: {
        name: 'Stone Floor',
        category: ObjectCategory.STRUCTURE,
        layer: ObjectLayer.FLOOR,
        blockMovement: false,
        blockSight: false,
        destructible: true,
        maxHealth: 100,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: false,
        buildCost: [{ resourceType: 'stone', amount: 4 }],
        buildTime: 30,
    },
    // Furniture
    [ObjectType.BED]: {
        name: 'Bed',
        category: ObjectCategory.FURNITURE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 80,
        flammable: true,
        providesShade: false,
        width: 1,
        height: 2,
        harvestable: false,
        buildable: true,
        buildCost: [
            { resourceType: 'wood', amount: 20 },
            { resourceType: 'cloth', amount: 10 },
        ],
        buildTime: 120,
    },
    [ObjectType.TABLE]: {
        name: 'Table',
        category: ObjectCategory.FURNITURE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 60,
        flammable: true,
        providesShade: false,
        width: 2,
        height: 1,
        harvestable: false,
        buildable: true,
        buildCost: [{ resourceType: 'wood', amount: 15 }],
        buildTime: 80,
    },
    [ObjectType.CHAIR]: {
        name: 'Chair',
        category: ObjectCategory.FURNITURE,
        layer: ObjectLayer.OBJECT,
        blockMovement: false,
        blockSight: false,
        destructible: true,
        maxHealth: 40,
        flammable: true,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: true,
        buildCost: [{ resourceType: 'wood', amount: 8 }],
        buildTime: 40,
    },
    [ObjectType.SLEEPING_PAD]: {
        name: 'Sleeping Pad',
        category: ObjectCategory.FURNITURE,
        layer: ObjectLayer.FLOOR, // Sits on ground
        blockMovement: false, // Pawns can walk over it
        blockSight: false,
        destructible: true,
        maxHealth: 30,
        flammable: true,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: true,
        buildCost: [{ resourceType: 'wood', amount: 5 }],
        buildTime: 20, // Very fast to build
    },
    [ObjectType.STORAGE_CRATE]: {
        name: 'Storage Crate',
        category: ObjectCategory.FURNITURE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 100,
        flammable: true,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: true,
        buildCost: [{ resourceType: 'wood', amount: 12 }],
        buildTime: 60,
    },
    [ObjectType.STORAGE_SHELF]: {
        name: 'Storage Shelf',
        category: ObjectCategory.FURNITURE,
        layer: ObjectLayer.TALL,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 80,
        flammable: true,
        providesShade: false,
        width: 2,
        height: 1,
        harvestable: false,
        buildable: false,
        buildCost: [{ resourceType: 'wood', amount: 20 }],
        buildTime: 80,
    },
    // Workstations
    [ObjectType.CRAFTING_TABLE]: {
        name: 'Crafting Table',
        category: ObjectCategory.WORKSTATION,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 120,
        flammable: true,
        providesShade: false,
        width: 2,
        height: 1,
        harvestable: false,
        buildable: true,
        buildCost: [{ resourceType: 'wood', amount: 30 }],
        buildTime: 150,
    },
    [ObjectType.ANVIL]: {
        name: 'Anvil',
        category: ObjectCategory.WORKSTATION,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 300,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: true,
        buildCost: [
            { resourceType: 'iron_bar', amount: 5 },
            { resourceType: 'stone', amount: 10 },
        ],
        buildTime: 200,
    },
    [ObjectType.FURNACE]: {
        name: 'Furnace',
        category: ObjectCategory.WORKSTATION,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 250,
        flammable: false,
        providesShade: false,
        width: 2,
        height: 2,
        harvestable: false,
        buildable: true,
        buildCost: [
            { resourceType: 'stone', amount: 30 },
            { resourceType: 'coal', amount: 10 },
        ],
        buildTime: 250,
    },
    [ObjectType.CAMPFIRE]: {
        name: 'Campfire',
        category: ObjectCategory.WORKSTATION,
        layer: ObjectLayer.OBJECT,
        blockMovement: false,
        blockSight: false,
        destructible: true,
        maxHealth: 50,
        flammable: false, // Already on fire
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: true,
        buildCost: [
            { resourceType: 'wood', amount: 5 },
            { resourceType: 'stone', amount: 5 },
        ],
        buildTime: 30,
    },
    [ObjectType.COOKING_STOVE]: {
        name: 'Cooking Stove',
        category: ObjectCategory.WORKSTATION,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 150,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: true,
        buildCost: [
            { resourceType: 'metal', amount: 20 },
            { resourceType: 'stone', amount: 10 },
        ],
        buildTime: 180,
    },
    // Resources/Ores
    [ObjectType.ORE_IRON]: {
        name: 'Iron Ore Deposit',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 500,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'iron_ore', minAmount: 6, maxAmount: 12 },
        buildable: false,
    },
    [ObjectType.ORE_GOLD]: {
        name: 'Gold Ore Deposit',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 400,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'gold_ore', minAmount: 3, maxAmount: 6 },
        buildable: false,
    },
    [ObjectType.ORE_COPPER]: {
        name: 'Copper Ore Deposit',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 350,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'copper_ore', minAmount: 5, maxAmount: 10 },
        buildable: false,
    },
    [ObjectType.ORE_TIN]: {
        name: 'Tin Ore Deposit',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 300,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'tin_ore', minAmount: 5, maxAmount: 10 },
        buildable: false,
    },
    [ObjectType.ORE_COAL]: {
        name: 'Coal Deposit',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 400,
        flammable: true,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'coal', minAmount: 8, maxAmount: 15 },
        buildable: false,
    },
    [ObjectType.ORE_SILVER]: {
        name: 'Silver Ore Deposit',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 450,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'silver_ore', minAmount: 4, maxAmount: 8 },
        buildable: false,
    },
    [ObjectType.ORE_MITHRIL]: {
        name: 'Mithril Ore Deposit',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 600,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'mithril_ore', minAmount: 2, maxAmount: 4 },
        buildable: false,
    },
    [ObjectType.ORE_STARMETAL]: {
        name: 'Starmetal Ore Deposit',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 800,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'starmetal_ore', minAmount: 1, maxAmount: 3 },
        buildable: false,
    },
    // Fishing Spots
    [ObjectType.FISHING_SPOT_SHORE]: {
        name: 'Shore Fishing Spot',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.GROUND,
        blockMovement: false,
        blockSight: false,
        destructible: false,
        maxHealth: 0,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: false,
    },
    [ObjectType.FISHING_SPOT_RIVER]: {
        name: 'River Fishing Spot',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.GROUND,
        blockMovement: false,
        blockSight: false,
        destructible: false,
        maxHealth: 0,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: false,
    },
    [ObjectType.FISHING_SPOT_OCEAN]: {
        name: 'Ocean Fishing Spot',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.GROUND,
        blockMovement: false,
        blockSight: false,
        destructible: false,
        maxHealth: 0,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: false,
    },
    [ObjectType.FISHING_SPOT_SWAMP]: {
        name: 'Swamp Fishing Spot',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.GROUND,
        blockMovement: false,
        blockSight: false,
        destructible: false,
        maxHealth: 0,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: false,
    },
    // Large ore deposits (Rich Veins) - respawn after depletion
    [ObjectType.ORE_TIN_LARGE]: {
        name: 'Rich Tin Vein',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 600,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'tin_ore', minAmount: 20, maxAmount: 40 },
        buildable: false,
    },
    [ObjectType.ORE_COPPER_LARGE]: {
        name: 'Rich Copper Vein',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 700,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'copper_ore', minAmount: 20, maxAmount: 40 },
        buildable: false,
    },
    [ObjectType.ORE_IRON_LARGE]: {
        name: 'Rich Iron Vein',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 1000,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'iron_ore', minAmount: 25, maxAmount: 45 },
        buildable: false,
    },
    [ObjectType.ORE_COAL_LARGE]: {
        name: 'Rich Coal Seam',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 800,
        flammable: true,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'coal', minAmount: 30, maxAmount: 50 },
        buildable: false,
    },
    [ObjectType.ORE_SILVER_LARGE]: {
        name: 'Rich Silver Vein',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 900,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'silver_ore', minAmount: 15, maxAmount: 30 },
        buildable: false,
    },
    [ObjectType.ORE_GOLD_LARGE]: {
        name: 'Rich Gold Vein',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 800,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'gold_ore', minAmount: 12, maxAmount: 25 },
        buildable: false,
    },
    [ObjectType.ORE_MITHRIL_LARGE]: {
        name: 'Rich Mithril Vein',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 1200,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'mithril_ore', minAmount: 8, maxAmount: 16 },
        buildable: false,
    },
    [ObjectType.ORE_STARMETAL_LARGE]: {
        name: 'Rich Starmetal Vein',
        category: ObjectCategory.RESOURCE,
        layer: ObjectLayer.OBJECT,
        blockMovement: true,
        blockSight: false,
        destructible: true,
        maxHealth: 1600,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: true,
        harvestYield: { resourceType: 'starmetal_ore', minAmount: 5, maxAmount: 12 },
        buildable: false,
    },
    // Territory Flags
    [ObjectType.CLAIM_FLAG_HOME]: {
        name: 'Home Flag',
        category: ObjectCategory.TERRITORY,
        layer: ObjectLayer.TALL,
        blockMovement: false,
        blockSight: false,
        destructible: false, // Home flags are indestructible
        maxHealth: 1000,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: true,
        buildCost: [
            { resourceType: 'wood', amount: 50 },
            { resourceType: 'stone', amount: 25 },
        ],
        buildTime: 200,
    },
    [ObjectType.CLAIM_FLAG_OUTPOST]: {
        name: 'Outpost Flag',
        category: ObjectCategory.TERRITORY,
        layer: ObjectLayer.TALL,
        blockMovement: false,
        blockSight: false,
        destructible: true, // Outpost flags can be destroyed
        maxHealth: 500,
        flammable: false,
        providesShade: false,
        width: 1,
        height: 1,
        harvestable: false,
        buildable: true,
        buildCost: [
            { resourceType: 'wood', amount: 30 },
            { resourceType: 'stone', amount: 15 },
        ],
        buildTime: 150,
    },
};
// Helper to get object properties
export const getObjectProperties = ({ objectType, }) => {
    return OBJECT_PROPERTIES[objectType] ?? OBJECT_PROPERTIES[ObjectType.NONE];
};
// Helper to check if object blocks tile
export const doesObjectBlockTile = ({ objectType }) => {
    const props = getObjectProperties({ objectType });
    return props.blockMovement;
};
// Helper to get object name for display
export const getObjectName = ({ objectType }) => {
    return getObjectProperties({ objectType }).name;
};
const DOOR_OBJECT_TYPES = new Set([ObjectType.DOOR_WOOD, ObjectType.DOOR_METAL]);
const WALL_OBJECT_TYPES = new Set([
    ObjectType.WALL_WOOD,
    ObjectType.WALL_STONE,
    ObjectType.WALL_METAL,
]);
export const isDoorObjectType = ({ objectType }) => {
    return DOOR_OBJECT_TYPES.has(objectType);
};
export const isWallObjectType = ({ objectType }) => {
    return WALL_OBJECT_TYPES.has(objectType);
};
export const isWallOrDoorObjectType = ({ objectType, }) => {
    return WALL_OBJECT_TYPES.has(objectType) || DOOR_OBJECT_TYPES.has(objectType);
};
//# sourceMappingURL=types.js.map